import React, { useState } from 'react';

/** Heuristic: ~twice the original 120-char / 2-line preview. */
const LONG_NOTE_CHARS = 240;
const PREVIEW_LINES = 4;

export interface ExpandableNotesProps {
  notes?: string | null;
  /** Shown when notes are empty; if omitted, renders nothing */
  emptyDisplay?: React.ReactNode;
  /** Compact styling for tight table cells */
  compact?: boolean;
}

const ExpandableNotes: React.FC<ExpandableNotesProps> = ({
  notes,
  emptyDisplay,
  compact = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const trimmed = notes?.trim() ?? '';

  if (!trimmed) {
    if (emptyDisplay === undefined) return null;
    return <>{emptyDisplay}</>;
  }

  const lineCount = trimmed.split('\n').length;
  const isLong = trimmed.length > LONG_NOTE_CHARS || lineCount > PREVIEW_LINES;
  const showClamped = isLong && !expanded;

  return (
    <div style={{ textAlign: 'left' }}>
      <div
        style={{
          fontSize: compact ? '0.75rem' : '0.875rem',
          fontWeight: 400,
          color: '#555',
          lineHeight: 1.35,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          ...(showClamped
            ? {
                display: '-webkit-box',
                WebkitLineClamp: PREVIEW_LINES,
                WebkitBoxOrient: 'vertical' as const,
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        {trimmed}
      </div>
      {isLong && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          style={{
            marginTop: '0.2rem',
            padding: 0,
            border: 'none',
            background: 'none',
            color: '#2980b9',
            fontSize: compact ? '0.7rem' : '0.8rem',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {expanded ? 'Less' : 'More'}
        </button>
      )}
    </div>
  );
};

export default ExpandableNotes;
